// OEM unit system — imperial (HP · lb-ft) vs metric (PS · Nm).
//
// Every power/torque figure on the site is rendered TWICE — once imperial,
// once metric — wrapped by OEMUnits.dual(). A single `data-oem-units`
// attribute on <html> decides which set is visible (see oem-overrides.css),
// so toggling never re-runs any render logic; it just flips the attribute.
//
// Mode resolution (first match wins):
//   1. the visitor's saved choice in localStorage ("oemUnits")
//   2. Shopify Markets geolocated country (window.OEM_COUNTRY) — US is imperial,
//      every other market is metric
//   3. browser locale — en-US defaults to imperial, everywhere else metric
//
// API (window.OEMUnits):
//   mode               -> "imperial" | "metric"
//   set(m) / toggle()  -> change mode (persists + re-syncs toggle buttons)
//   nm(lbft)           -> torque number in Nm  (lb-ft * 1.35582, rounded)
//   psFromHp(hp)       -> PS number from an HP value (hp * 1.01387, rounded)
//   psFromCode(code)   -> PS number from an engine code ("P615" -> 615)
//   dual(impHTML, metHTML) -> "<span imp>…</span><span met>…</span>"
//   toggleHTML()       -> markup for one toggle control
//   mountToggles()     -> fill every [data-oem-units-toggle-host] with a toggle
//                         (call again after injecting markup dynamically)
(function () {
  "use strict";

  var KEY = "oemUnits";
  var LB_FT_TO_NM = 1.35582; // 1 lb-ft = 1.35582 N·m
  var HP_TO_PS = 1.01387;    // 1 HP = 1.01387 PS (metric horsepower)
  var doc = document;
  var root = doc.documentElement;

  function detectDefault() {
    // Primary: Shopify Markets geolocated country (US market = imperial).
    try {
      var c = (window.OEM_COUNTRY || "").toUpperCase();
      if (c) return c === "US" ? "imperial" : "metric";
    } catch (e) {}
    // Fallback: browser locale, when the country wasn't rendered.
    try {
      var lang = (navigator.language || "").toLowerCase();
      if (lang === "en-us" || lang.indexOf("en-us") === 0) return "imperial";
    } catch (e) {}
    return "metric";
  }

  function stored() {
    try { return localStorage.getItem(KEY); } catch (e) { return null; }
  }

  var saved = stored();
  var mode = (saved === "imperial" || saved === "metric") ? saved : detectDefault();

  // Set the attribute immediately (this script loads in <head>, before paint)
  // so metric visitors never see an imperial flash.
  root.setAttribute("data-oem-units", mode);

  function syncToggles() {
    var groups = doc.querySelectorAll("[data-oem-units-toggle]");
    for (var i = 0; i < groups.length; i++) {
      var btns = groups[i].querySelectorAll("[data-units]");
      for (var j = 0; j < btns.length; j++) {
        var on = btns[j].getAttribute("data-units") === mode;
        btns[j].classList.toggle("is-active", on);
        btns[j].setAttribute("aria-pressed", on ? "true" : "false");
      }
    }
  }

  function set(next) {
    if (next !== "imperial" && next !== "metric" || next === mode) return;
    mode = next;
    try { localStorage.setItem(KEY, mode); } catch (e) {}
    root.setAttribute("data-oem-units", mode);
    syncToggles();
  }

  function num(v) {
    var n = parseFloat(v);
    return isFinite(n) ? n : null;
  }

  function toggleHTML() {
    return (
      '<div class="oem-units-toggle" data-oem-units-toggle role="group" aria-label="Units">' +
      '<button type="button" class="oem-units-toggle__btn" data-units="imperial">HP · lb-ft</button>' +
      '<button type="button" class="oem-units-toggle__btn" data-units="metric">PS · Nm</button>' +
      "</div>"
    );
  }

  function mountToggles() {
    var hosts = doc.querySelectorAll("[data-oem-units-toggle-host]");
    for (var i = 0; i < hosts.length; i++) {
      if (!hosts[i].querySelector("[data-oem-units-toggle]")) {
        hosts[i].innerHTML = toggleHTML();
      }
    }
    syncToggles();
  }

  window.OEMUnits = {
    get mode() { return mode; },
    set: set,
    toggle: function () { set(mode === "imperial" ? "metric" : "imperial"); },
    nm: function (lbft) {
      var n = num(lbft);
      return n == null ? lbft : Math.round(n * LB_FT_TO_NM);
    },
    psFromHp: function (hp) {
      var n = num(hp);
      return n == null ? hp : Math.round(n * HP_TO_PS);
    },
    psFromCode: function (code) {
      var m = String(code == null ? "" : code).match(/\d+/);
      return m ? parseInt(m[0], 10) : null;
    },
    dual: function (impHTML, metHTML) {
      return (
        '<span class="oem-u oem-u--imp">' + impHTML + "</span>" +
        '<span class="oem-u oem-u--met">' + metHTML + "</span>"
      );
    },
    toggleHTML: toggleHTML,
    mountToggles: mountToggles
  };

  // One delegated handler covers every toggle, including any injected later.
  doc.addEventListener("click", function (e) {
    var btn = e.target.closest ? e.target.closest("[data-oem-units-toggle] [data-units]") : null;
    if (btn) set(btn.getAttribute("data-units"));
  });

  if (doc.readyState === "loading") {
    doc.addEventListener("DOMContentLoaded", mountToggles);
  } else {
    mountToggles();
  }
})();
